import React, { useEffect, useState } from "react";
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  CircularProgress,
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useNavigate, useLocation } from "react-router-dom";
import { useIntl } from "react-intl";

import { TREE_ICONS, DESIGN_CHILDREN } from "../../constants/treeConfig";
import { fetchBusinessDomains } from "../../api/companyApi";
import { COMPANY_ID } from "../../config/appConfig";

const DesignIcon = TREE_ICONS.design;
const DomainIcon = TREE_ICONS.businessDomain;

export default function DesignTree() {
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  const [designOpen, setDesignOpen] = useState(true);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedDomains, setExpandedDomains] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    loadDomains();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const loadDomains = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchBusinessDomains(COMPANY_ID);
      setDomains(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load business domains:", e);
      setError(e.message || "API接続エラー");
    } finally {
      setLoading(false);
    }
  };

  const toggleDomain = (domainId) => {
    setExpandedDomains((prev) => ({ ...prev, [domainId]: !prev[domainId] }));
  };

  const toggleGroup = (domainId, groupKey) => {
    const key = `${domainId}_${groupKey}`;
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const go = (path) => {
    navigate(path);
  };

  const isActive = (path) => location.pathname === path;

  const activeStyle = {
    backgroundColor: "rgba(21, 101, 192, 0.08)",
    color: "#1565C0",
    fontWeight: 700,
    borderLeft: "3px solid #1565C0",
  };

  /** Render a leaf node */
  const renderLeafNode = (child, domainId, pl) => {
    const ChildIcon = child.icon;
    const path = `/design/${domainId}/${child.route}`;
    return (
      <ListItemButton
        key={child.key}
        sx={{ pl, ...(isActive(path) ? activeStyle : {}) }}
        onClick={() => go(path)}
      >
        <ListItemIcon sx={{ minWidth: 32 }}>
          <ChildIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary={t(child.labelKey)}
          primaryTypographyProps={{ fontSize: "0.8rem" }}
        />
      </ListItemButton>
    );
  };

  /** Render a group node (expandable with children) */
  const renderGroupNode = (child, domain) => {
    const domainId = domain.businessDomainId;
    const GroupIcon = child.icon;
    const groupKey = `${domainId}_${child.key}`;
    const isExpanded = expandedGroups[groupKey] || false;

    // Filter out enterpriseOnly children when domain is not enterprise
    const visibleChildren = child.children.filter(
      (gc) => !gc.enterpriseOnly || domain.enterprise
    );

    // Hide the entire group if all children are filtered out
    if (visibleChildren.length === 0) return null;

    return (
      <React.Fragment key={child.key}>
        <ListItemButton
          sx={{ pl: 5 }}
          onClick={() => toggleGroup(domainId, child.key)}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <GroupIcon fontSize="small" color="action" />
          </ListItemIcon>
          <ListItemText
            primary={t(child.labelKey)}
            primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 500 }}
          />
          {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </ListItemButton>

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List dense disablePadding>
            {visibleChildren.map((gc) => renderLeafNode(gc, domainId, 7))}
          </List>
        </Collapse>
      </React.Fragment>
    );
  };

  /** Render design children for a domain (supports groups and leaf nodes) */
  const renderDesignChildren = (domain) => {
    const domainId = domain.businessDomainId;
    return DESIGN_CHILDREN.map((child) => {
      if (child.group) {
        return renderGroupNode(child, domain);
      }
      // Leaf node: check enterpriseOnly
      if (child.enterpriseOnly && !domain.enterprise) return null;
      return renderLeafNode(child, domainId, 5);
    });
  };

  return (
    <List dense disablePadding>
      {/* Design root node */}
      <ListItemButton onClick={() => setDesignOpen(!designOpen)} sx={{ fontWeight: 600 }}>
        <ListItemIcon sx={{ minWidth: 36 }}>
          <DesignIcon color="primary" />
        </ListItemIcon>
        <ListItemText primary={t("tree.design")} primaryTypographyProps={{ fontWeight: 600 }} />
        <Tooltip title={t("businessDomain.create.title")}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              navigate("/design/business-domains/create");
            }}
            sx={{ mr: 0.5 }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {designOpen ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>

      <Collapse in={designOpen} timeout="auto" unmountOnExit>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : error ? (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>
              {error}
            </Typography>
            <Button size="small" startIcon={<RefreshIcon />} onClick={loadDomains}>
              再読込
            </Button>
          </Box>
        ) : domains.length === 0 ? (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              事業ドメインがありません
            </Typography>
            <Button size="small" startIcon={<RefreshIcon />} onClick={loadDomains}>
              再読込
            </Button>
          </Box>
        ) : (
          <List dense disablePadding>
            {domains.map((domain) => (
              <React.Fragment key={domain.businessDomainId}>
                {/* Business Domain node */}
                <ListItemButton
                  sx={{ pl: 3 }}
                  onClick={() => toggleDomain(domain.businessDomainId)}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <DomainIcon fontSize="small" color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary={domain.businessDomainName}
                    primaryTypographyProps={{ fontSize: "0.875rem", noWrap: true }}
                  />
                  {expandedDomains[domain.businessDomainId] ? (
                    <ExpandLess fontSize="small" />
                  ) : (
                    <ExpandMore fontSize="small" />
                  )}
                </ListItemButton>

                {/* Category children under each domain */}
                <Collapse
                  in={expandedDomains[domain.businessDomainId]}
                  timeout="auto"
                  unmountOnExit
                >
                  <List dense disablePadding>
                    {/* BusinessDomain detail link */}
                    <ListItemButton
                      sx={{
                        pl: 5,
                        ...(isActive(`/design/${domain.businessDomainId}`) ? activeStyle : {}),
                      }}
                      onClick={() => go(`/design/${domain.businessDomainId}`)}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <DomainIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={t("businessDomain.title")}
                        primaryTypographyProps={{ fontSize: "0.8rem" }}
                      />
                    </ListItemButton>

                    {renderDesignChildren(domain)}
                  </List>
                </Collapse>
              </React.Fragment>
            ))}
          </List>
        )}
      </Collapse>
    </List>
  );
}
